/**
 * B-337 (2026-10-01, owner): where a visitor came from.
 *
 * Owner asked "how many visitors, from where they arrived": our own events
 * carried no referring site and no campaign tag, so the answer existed only
 * in Google Analytics. This reads the arrival facts of ONE visit from the
 * page address and the browser's referrer, as plain data, no personal data:
 *
 *   referrer_host  the referring site's host only ("www.google.com"); never
 *                  its path or query, which can carry search words
 *   utm_source / utm_medium / utm_campaign   campaign tags on the link
 *   click_id       the NAME of an ad or social click id on the link
 *                  ("fbclid", "gclid"), never its value. Facebook and
 *                  Instagram in-app browsers often send no referrer; their
 *                  fbclid still says where the visit came from
 *   landing_path   the first page's path only, never its query string
 *
 * Pure (no window access) so it can be checked on its own.
 */

export interface VisitSource {
  referrer_host: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  click_id: string | null;
  landing_path: string;
}

/** Ad and social click ids, in the order they are checked. Names only. */
const CLICK_IDS = ["gclid", "gbraid", "wbraid", "fbclid", "msclkid", "ttclid", "li_fat_id", "twclid", "dclid"] as const;

function cap(v: string | null | undefined, n: number): string | null {
  const t = (v ?? "").trim();
  return t ? t.slice(0, n) : null;
}

/** Hosts that are part of signing in or of our own site: a page reached from
 *  them is not a new arrival from outside (2026-10-02). */
const INTERNAL_HOST_RX = /(^|\.)octopusden\.app$|^localhost(:\d+)?$|^127\.0\.0\.1(:\d+)?$|(^|\.)clerk\.accounts\.dev$|(^|\.)clerk\.[a-z0-9.-]+$|^accounts\.google\.com$|^appleid\.apple\.com$/;

/**
 * True when this arrival came from outside with a known source: another
 * site's referrer, a campaign tag, or an ad / social click id. A visit with
 * no referrer and no tags (typed address, an app, an email) is not a known
 * source; neither is a page reached from our own site or from a sign-in step.
 */
export function isOutsideArrival(s: VisitSource, ownHost: string): boolean {
  if (s.utm_source || s.utm_medium || s.utm_campaign || s.click_id) return true;
  const h = (s.referrer_host || "").toLowerCase();
  if (!h) return false;
  if (h === (ownHost || "").toLowerCase()) return false;
  return !INTERNAL_HOST_RX.test(h);
}

export function visitSource(href: string, referrer: string): VisitSource {
  let url: URL | null = null;
  try {
    url = new URL(href);
  } catch {
    url = null;
  }
  let refHost: string | null = null;
  try {
    refHost = referrer ? new URL(referrer).host.toLowerCase() : null;
  } catch {
    refHost = null;
  }
  const p = url?.searchParams;
  const clickId = p ? CLICK_IDS.find((k) => p.has(k)) ?? null : null;
  return {
    referrer_host: cap(refHost, 255),
    utm_source: cap(p?.get("utm_source"), 100),
    utm_medium: cap(p?.get("utm_medium"), 100),
    utm_campaign: cap(p?.get("utm_campaign"), 100),
    click_id: clickId,
    landing_path: (url?.pathname || "/").slice(0, 255),
  };
}
